import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";

// API's
import { getSalesPerformanceReportVersions } from "@/Common/api/salesPerformance/queries";

// Helpers
import { supportedSystemPositions } from "@Common/helpers/Positions";
import { findInParentInTree } from "@Common/helpers/Arrays/tree";

// Hooks
import { usePayPlansDefaults, buildPayPlan } from "@PayPlans/hooks/useSectionsDefaults";

// Contexts
import { getPayPlanPosition, usePayPlan } from "@PayPlans/context/PayPlans";
import { useSubscriptions } from "@Common/context/subscriptions/Subscriptions";

// Components
import AppSideBar from "@/Layouts/components/SideBars/AppSideBar";
import SideInformation from "@PayPlans/Layouts/PayPlansSideBar/SideInformation";
import MatchesItem from "@/Layouts/components/SideBars/SideSelectorMenu/MatchesItem";

// Icons
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import AddCircleRounded from "@mui/icons-material/AddCircleRounded";
import RemoveCircleRoundedIcon from "@mui/icons-material/RemoveCircleRounded";
import RemoveCircleOutlineRoundedIcon from "@mui/icons-material/RemoveCircleOutlineRounded";
import OptionsMenuIcon from "@/assets/icons/svg/OptionsMenuIcon";

// Constants
const POSITIONS = supportedSystemPositions();
const PROGRESS_LABEL = "Make In Progress";
const AVAILABLE_LABEL = "Make Available";
const OPTIONS_ICON_CLASSES = "side-selector-more-btn";

const hasSalesReportVersions = [POSITIONS.SALES.SALES_ASSOCIATE, POSITIONS.FINANCE.FINANCE_MANAGER];

const getOptionMenusClasses = (disabled = false) => {
	const added = disabled ? "!text-disabled" : "!text-default";
	return `!opacity-100 !font-sans text-[16px] ${added}`;
};

const getMappedMatches = (list = [], matches = undefined, loadingMatches = false, available = false) => {
	return list.map((item) => {
		return {
			...item,
			available,
			customComponent: {
				component: <MatchesItem />,
				loading: loadingMatches,
				matches,
			},
		};
	});
};

const getMappedMainTree = (payPlansTree = null, matches = undefined, loadingMatches = false) => {
	const { currentlyAssigned = [], previouslyAssigned = [], available = [], inProgress = [] } = payPlansTree || {};

	const currentList = getMappedMatches(currentlyAssigned, matches, loadingMatches);
	const previouslyList = getMappedMatches(previouslyAssigned, matches, loadingMatches);
	const availableList = getMappedMatches(available, matches, loadingMatches, true);
	const inProgressList = getMappedMatches(inProgress, matches, loadingMatches);

	return [
		{
			_id: "previouslyAssigned",
			name: "Previously Assigned",
			bold: true,
			root: true,
			disabled: !previouslyList?.length,
			list: previouslyList,
		},
		{
			_id: "currentlyAssigned",
			name: "Currently Assigned",
			bold: true,
			root: true,
			disabled: !currentList?.length,
			list: currentList,
		},
		{
			_id: "available",
			name: "Available for Assignment",
			bold: true,
			root: true,
			disabled: !availableList?.length,
			list: availableList,
		},
		{
			_id: "inProgress",
			name: "In Progress",
			bold: true,
			root: true,
			disabled: !inProgressList?.length,
			list: inProgressList,
		},
	];
};

const getPayPlanSectionIndex = (payPlan, identifier) => {
	if (!payPlan || !identifier) {
		return {
			section: "",
			index: -1,
		};
	}

	for (const key of Object.keys(payPlan)) {
		if (payPlan[key]?.length && payPlan[key].some((item) => item._id === identifier)) {
			const index = payPlan[key]?.findIndex((item) => item._id === identifier);
			return {
				section: key,
				index,
			};
		}
	}

	return {
		section: "",
		index: -1,
	};
};

const buildDefaultTree = (defaultPayPlans, inProgress = []) => {
	const keyToLabel = {
		newVehiclePayPlan: "NEW",
		preOwnedVehiclePayPlan: "USED",
	};

	if (!defaultPayPlans) {
		return [];
	}

	return Object.keys(defaultPayPlans)
		.map((key) => {
			const available = !inProgress.some((item) => item._id === defaultPayPlans[key]?._id);
			if (available && defaultPayPlans[key]) {
				return {
					...defaultPayPlans[key],
					label: keyToLabel[key] || null,
					available,
				};
			}
			return null;
		})
		.filter(Boolean);
};

const getDefaultMappedTree = (tree = [], matches = undefined, loadingMatches = false) => {
	return tree?.map((item) => {
		return {
			...item,
			customComponent: {
				component: <SideInformation />,
				isLoadingComponent: loadingMatches,
				matches,
			},
		};
	});
};

const getDefaultSelectedItems = (tree = [], selected = "") => {
	const selectedItems = tree?.filter((item) => item._id === selected);
	return selectedItems?.map((item) => item._id);
};

export default function PayPlansSideBar(props) {
	const { showBy = "name", selectBy = "_id", loading = false } = props;

	const location = useSelector((state) => state.location.location);
	const user = useSelector((state) => state.user.user);
	const queryClient = useQueryClient();
	const { getSubscription } = useSubscriptions();

	const [dataTree, setDataTree] = useState([]);
	const [defaultSelectedId, setDefaultSelectedId] = useState("");
	const [availableToggleLabel, setAvailableToggleLabel] = useState("");
	const [defaultTree, setDefaultTree] = useState([]);
	const [expanded, setExpanded] = useState([]);
	const [isDefaultSelected, setIsDefaultSelected] = useState(false);

	const {
		defaultSections,
		services,
		position,
		positionId,
		positionType,
		isDefaultEditable,
		isLoadingModel,
		isPayPlanLocked,
		isNewPayPlan,
		isValid,
		isSomeNewPayPlan,
		selected,
		setInitialModel,
		setUpdatedSections,
		setSelected,
		setIsPayPlanAvailable,
		setSomeNewPayPlan,
		setSelections,
		selectionIds,
		isEditing,
	} = usePayPlan();

	const { queries, mutations, subscriptions } = services;
	const isNew = isNewPayPlan();

	const defaults = usePayPlansDefaults(position, positionType, { location, components: defaultSections });
	const onClonePayPlan = subscriptions?.get("onClonePayPlan");
	const onCreatePayPlan = subscriptions?.get("onCreatePayPlan");
	const onDeletePayPlan = subscriptions?.get("onDeletePayPlan");
	const onUpdatePayPlan = subscriptions?.get("onUpdatePayPlan");
	const onUpdateAvailablePayPlan = subscriptions?.get("onUpdateAvailablePayPlan");

	// Special callback handler
	const updateSelectionTree = useCallback(
		(currentPositionId, payPlan) => {
			queryClient.setQueryData(["payPlan", "list", currentPositionId, payPlan?.locationId], (previous) => {
				const data = previous;
				let section = "";
				let foundName = null;
				for (const key of Object.keys(data)) {
					if (data[key]?.length && data[key].some((item) => item.name === payPlan.name)) {
						section = key;
						foundName = data[key].some((item) => item.name === payPlan.name);
						break;
					}
				}
				if (foundName) {
					// If pay plan exist change the element identifier by the new one
					data[section].forEach((element, key) => {
						if (element.name === payPlan.name && element._id?.includes("new_")) {
							data[key]["_id"] = payPlan._id;
						}
					});
				} else {
					// Add to the tree if not in the list
					data?.inProgress?.push({
						_id: payPlan._id,
						name: payPlan.name,
						position: payPlan.position,
					});
				}
				return data;
			});
		},
		[queryClient]
	);

	// Subscriptions
	for (const id of selectionIds) {
		const payPlansSelectedSubscription = getSubscription(["payPlanSelectedSubscription", positionId, location?.locationId, id]);

		payPlansSelectedSubscription?.subscriptions?.onUpdatePayPlan?.setCallback((result) => {
			const payPlan = result?.data?.[onUpdatePayPlan?.getter];
			if (payPlan && payPlan.updatedBy?._id !== user._id) {
				queryClient.setQueryData(["payPlan", positionId, payPlan?.locationId, payPlan?._id], (previous) => {
					return {
						...previous,
						...payPlan,
					};
				});
			}
			refetch();
		});
	}

	const payPlansSubscriptions = getSubscription(["payPlanSubscriptions", positionId, location?.locationId]);
	payPlansSubscriptions?.subscriptions?.onClonePayPlan?.setCallback((result) => {
		const payPlan = result?.data?.[onClonePayPlan?.getter];
		if (payPlan) {
			const positionPayPlanId = getPayPlanPosition(payPlan.position);
			queryClient.setQueryData(["payPlan", "list", positionPayPlanId, payPlan?.locationId], (previous) => {
				const data = previous;
				data?.inProgress?.push(payPlan);
				return data;
			});
			refetch();
		}
	});

	payPlansSubscriptions?.subscriptions?.onCreatePayPlan?.setCallback((result) => {
		const payPlan = result?.data?.[onCreatePayPlan?.getter];
		if (payPlan) {
			const currentPositionId = getPayPlanPosition(payPlan.position);
			updateSelectionTree(currentPositionId, payPlan);
			refetch();
		}
	});

	payPlansSubscriptions?.subscriptions?.onUpdateAvailablePayPlan?.setCallback((result) => {
		const payPlan = result?.data?.[onUpdateAvailablePayPlan?.getter];
		if (payPlan) {
			const currentPositionId = getPayPlanPosition(payPlan.position);
			queryClient.setQueryData(["payPlan", "list", currentPositionId, payPlan?.locationId], (previous) => {
				const data = previous;

				const addPositionTree = payPlan.available ? "available" : "inProgress";
				const removePositionTree = payPlan.available ? "inProgress" : "available";
				const index = data?.[removePositionTree]?.findIndex((item) => item._id === payPlan._id);
				if (index > -1) {
					data?.[removePositionTree]?.splice(index, 1);
				}

				data?.[addPositionTree]?.push(payPlan);

				return data;
			});
			refetch();
		}
	});

	payPlansSubscriptions?.subscriptions?.onDeletePayPlan?.setCallback((result) => {
		const payPlan = result?.data?.[onDeletePayPlan?.getter];
		if (payPlan) {
			const currentPositionId = getPayPlanPosition(payPlan.position);
			queryClient.setQueriesData({ queryKey: ["payPlan", currentPositionId, payPlan?.locationId, payPlan?._id] }, null);
			removeSelection(currentPositionId, payPlan?.locationId, payPlan);
		}
	});

	payPlansSubscriptions?.subscriptions?.onUpdateAnyPayPlan?.setCallback(() => {
		refetch();
	});

	// Queries
	const { data: payPlansSidebarTree, isLoading: isLoadingPayPlansSidebarTree } = useQuery({
		queryKey: ["payPlansSidebarTree", "list", positionId, location?.locationId],
		queryFn: async () => queries?.getPayPlansSidebarTree(location.locationId, position) || [],
		staleTime: Infinity,
		enabled: !!location?.locationId && !!positionId,
	});

	const {
		data: matches,
		isLoading: isLoadingMatches,
		isFetching,
		refetch,
	} = useQuery({
		queryKey: ["payPlan", "matches", positionId, location?.locationId, selected?._id],
		queryFn: async () => queries?.getMatches(location.locationId, selected?._id, selected?.position),
		staleTime: Infinity,
		enabled: !!location?.locationId && !!selected?._id,
	});

	const isPositionWithSalesReportVersions = hasSalesReportVersions?.includes(position);

	const { data: salesReportVersions = [] } = useQuery({
		queryKey: ["payPlanSalesPerformanceReportVersions", location?.locationId, position],
		queryFn: async () => getSalesPerformanceReportVersions(location?.locationId),
		staleTime: Infinity,
		enabled: !!location?.locationId && isPositionWithSalesReportVersions,
	});

	const isDisabledBySalesReportVersion = isPositionWithSalesReportVersions ? !salesReportVersions?.length : false;

	// Mutations
	const { mutate: addPayPlan, isPending: addStatus } = useMutation({
		mutationFn: (input) => mutations?.addPayPlan(input),
	});
	const { mutate: deletePayPlan, isPending: deleteStatus } = useMutation({
		mutationFn: (input) => mutations?.deletePayPlan(input),
	});
	const { mutate: clonePayPlan, isPending: cloneStatus } = useMutation({
		mutationFn: (input) => mutations?.clonePayPlan(input),
	});
	const { mutate: updateAvailablePayPlan, isPending: updateAvailableStatus } = useMutation({
		mutationFn: (input) => mutations?.updateAvailablePayPlan(input),
	});

	const isPerformingAction = useMemo(
		() => [addStatus, deleteStatus, cloneStatus, updateAvailableStatus, isLoadingModel].some((item) => item),
		[addStatus, cloneStatus, deleteStatus, isLoadingModel, updateAvailableStatus]
	);

	// Callbacks
	const handleSelected = useCallback(
		(selection, selectionExpanded = []) => {
			if (selection._id !== selected?._id) {
				if (Array.isArray(selectionExpanded)) {
					setExpanded(selectionExpanded);
				}

				setDefaultSelectedId(selection._id);
				setIsDefaultSelected(false);
				setSelected(selection);
				setUpdatedSections(null);
				setSelections(selection?._id);
			}
		},
		[selected, setSelected, setUpdatedSections, setSelections]
	);

	const removeSelection = useCallback(
		(currentPositionId, locationId, payPlanSelected) => {
			queryClient.setQueryData(["payPlansSidebarTree", "list", currentPositionId, locationId], (previous) => {
				if (previous) {
					const { section, index } = getPayPlanSectionIndex(previous, payPlanSelected?._id);
					const data = previous;
					if (index !== -1) {
						data?.[section]?.splice(index, 1);
					}

					if (data.length && index !== -1) {
						const selectFromIndex = index ? index - 1 : index;
						if (selected?._id === payPlanSelected?._id) {
							// Select current index
							handleSelected(data?.[selectFromIndex]);
						}
					} else {
						setSelected(null);
					}
					setInitialModel(null);
					return data;
				}
			});
		},
		[selected, queryClient, setInitialModel, setSelected, handleSelected]
	);

	const handleAdd = () => {
		// Request new pay plan
		addPayPlan(
			{ locationId: location?.locationId, position },
			{
				onSuccess: (response) => {
					if (response) {
						const { name, createdAt } = response;
						const payPlan = {
							_id: `new_${name}`,
							name,
							position,
						};

						let model = null;

						queryClient.setQueryData(["payPlansSidebarTree", "list", positionId, location?.locationId], (previous) => {
							const data = { ...previous };
							if (data?.inProgress?.length) {
								data.inProgress.push(payPlan);
							} else {
								data.inProgress = [payPlan];
							}
							return data;
						});

						queryClient.setQueryData(["payPlan", positionId, location?.locationId, payPlan?._id], () => {
							model = buildPayPlan(payPlan, defaults, { user, creation: createdAt });
							return model;
						});

						setInitialModel(model);
						setSelected(payPlan);
						setSomeNewPayPlan(true);
						setDefaultSelectedId("");
						setExpanded(["inProgress"]);
					}
				},
				onError: (error) => {
					console.log(error);
				},
			}
		);
	};

	const handleDelete = () => {
		removeSelection(positionId, location?.locationId, selected);
		if (isNew) {
			setSomeNewPayPlan(false);
		} else {
			deletePayPlan(
				{ locationId: location?.locationId, _id: selected?._id, position },
				{
					onSuccess: () => {
						setAvailableToggleLabel("");
					},
					onError: (error) => {
						console.log(error);
					},
				}
			);
		}
	};

	const handleClone = () => {
		clonePayPlan(
			{
				locationId: location?.locationId,
				information: {
					_id: selected?._id,
					createdBy: user?._id,
					position: selected?.position,
				},
				actualPosition: position,
			},
			{
				onSuccess: (response) => {
					const payPlan = {
						_id: response?._id,
						name: response?.name,
						position,
					};

					let model = null;

					queryClient.invalidateQueries(["payPlan", "matches"]);
					queryClient.setQueryData(["payPlansSidebarTree", "list", positionId, location?.locationId], (previous) => {
						const data = { ...previous };
						data?.inProgress?.push(payPlan);
						return data;
					});

					queryClient.setQueryData(["payPlan", positionId, location?.locationId, payPlan?._id], () => {
						model = { ...response };
						return model;
					});

					setInitialModel(model);
					setSelected(payPlan);
					setDefaultSelectedId("");
					setExpanded(["inProgress"]);
				},
				onError: (error) => {
					console.log(error);
				},
			}
		);
	};

	const handleToggleAvailable = () => {
		if (isPayPlanLocked) {
			return;
		}

		updateAvailablePayPlan(
			{
				locationId: location?.locationId,
				information: {
					_id: selected?._id,
					updatedBy: user?._id,
					available: !selected?.available,
				},
				position,
			},
			{
				onSuccess: (response) => {
					const payPlan = {
						_id: response._id,
						name: response.name,
						available: response.available,
						position,
					};

					// Update Tree data
					queryClient.setQueryData(["payPlansSidebarTree", "list", positionId, location?.locationId], (previous) => {
						const data = previous;

						const addPositionTree = payPlan.available ? "available" : "inProgress";
						const removePositionTree = payPlan.available ? "inProgress" : "available";
						const index = data?.[removePositionTree]?.findIndex((item) => item._id === payPlan._id);
						if (index > -1) {
							data?.[removePositionTree]?.splice(index, 1);
						}

						data?.[addPositionTree]?.push(payPlan);
						return data;
					});

					// Update Pay Plan data
					if (payPlan) {
						const { available } = payPlan;
						queryClient.setQueryData(["payPlan", positionId, location?.locationId, payPlan?._id], (previous) => {
							return { ...previous, available };
						});
					}

					// Update local state and context
					setSelected(payPlan);
					setExpanded(payPlan?.available ? ["available"] : ["inProgress"]);
					setIsPayPlanAvailable(payPlan?.available);
					setAvailableToggleLabel(payPlan?.available ? PROGRESS_LABEL : AVAILABLE_LABEL);
				},
				onError: (error) => {
					console.log(error);
				},
			}
		);
	};

	const handleSelection = (selItem, selectionExpanded = []) => {
		handleSelected(selItem, selectionExpanded);
		setIsDefaultSelected(true);
	};

	const handleTreeItemClick = (items = []) => {
		if (items?.length) {
			const itemSelected = items[0];
			const parent = findInParentInTree(dataTree, { id: itemSelected?.[selectBy], index: selectBy });
			const selectionExpanded = parent ? [parent?._id] : [];
			handleSelection(itemSelected, selectionExpanded);
		}
	};

	const loadingMatches = useMemo(() => loading || isLoadingMatches || isFetching, [loading, isLoadingMatches, isFetching]);
	const loadingTree = loading || isLoadingPayPlansSidebarTree;

	useEffect(() => {
		if (!location) {
			return;
		}

		if (payPlansSidebarTree) {
			const mainTree = getMappedMainTree(payPlansSidebarTree, matches, loadingMatches);
			setDataTree(mainTree);

			const defaultPayPlansTree = buildDefaultTree(payPlansSidebarTree.defaultPayPlans, payPlansSidebarTree?.inProgress);
			setDefaultTree(defaultPayPlansTree);
		}

		if (selected) {
			setAvailableToggleLabel(selected.available ? PROGRESS_LABEL : AVAILABLE_LABEL);
		}

		return () => {
			setDataTree([]);
			setDefaultTree([]);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props, location, payPlansSidebarTree, loadingMatches]);

	const disableFirstPart = !isDefaultEditable || !isValid || isSomeNewPayPlan;
	const disableSecondPart = isPerformingAction || isDisabledBySalesReportVersion;
	const isEditingOrSaving = isEditing || isPerformingAction;
	const isNotSelectedPosition = position !== selected?.position;
	const isNotEditable = !isDefaultEditable || isPayPlanLocked;
	const isDeleteDisabled = isNotEditable || isDefaultSelected || isEditingOrSaving || isNotSelectedPosition;

	// Operations action control
	const isCloneDisabled = disableFirstPart || isPerformingAction || !selected;
	const isSwitchAvailabilityDisabled = (isCloneDisabled && !selected?.available) || isNotSelectedPosition || isPayPlanLocked;
	const isMakeAvailableDisabled = isSwitchAvailabilityDisabled || availableToggleLabel !== AVAILABLE_LABEL;
	const isMakeInProgressDisabled = isSwitchAvailabilityDisabled || availableToggleLabel !== PROGRESS_LABEL;
	const isToggleDisabled = isCloneDisabled && isMakeAvailableDisabled && isMakeInProgressDisabled;

	return (
		<AppSideBar
			searchProps={{
				searchBy: showBy,
				loading: loadingTree,
				onChangeSearchAction: (value) => {
					if (value && selected) {
						setSelected(null);
						setDefaultSelectedId("");
					}
				},
			}}
			appendProps={{
				selectBy,
				showBy,
				loadingMatches,
				matches,
				label: {
					title: "Default Pay Plans",
					emptyTitle: "No Default Pay Plan Set",
					isEmpty: ({ tree: innerTree }) => !innerTree?.length,
				},
				divider: true,
				selectedItems: getDefaultSelectedItems(defaultTree, defaultSelectedId),
				tree: getDefaultMappedTree(defaultTree, matches, loadingMatches),
				loading: loadingTree,
				loadingRows: 2,
				onSelectAction: handleTreeItemClick,
			}}
			sideBarProps={{
				selectBy,
				showBy,
				expanded,
				height: "400px",
				tree: dataTree,
				selected: selected?.[selectBy] || "",
				loading: loadingTree,
				onSelectAction: handleSelected,
			}}
			actions={{
				spacing: 3,
				disabled: !isDefaultEditable || isSomeNewPayPlan || isEditingOrSaving,
				operations: [
					{
						identifier: "delete",
						classes: "btn-delete",
						color: "var(--gray-blue-color)",
						icon: <RemoveCircleOutlineRoundedIcon sx={{ fontSize: "35px" }} />,
						iconHover: <RemoveCircleRoundedIcon sx={{ fontSize: "35px" }} />,
						disabled: isDeleteDisabled || deleteStatus,
						doubleCheckingAction: true,
						loading: loadingTree,
						onClick: handleDelete,
					},
					{
						identifier: "add",
						classes: "btn-add",
						color: "var(--primary-color)",
						icon: <AddCircleOutlineRoundedIcon sx={{ fontSize: "35px" }} />,
						iconHover: <AddCircleRounded sx={{ fontSize: "35px" }} />,
						disabled: disableFirstPart || disableSecondPart || addStatus,
						loading: loadingTree,
						onClick: handleAdd,
					},
					{
						identifier: "options",
						classes: "h-[51px] w-[51px] less-opacity-disabled",
						color: "var(--gray-blue-color)",
						icon: <OptionsMenuIcon classes={OPTIONS_ICON_CLASSES} disabled={isToggleDisabled} sx={{ fontSize: "29px" }} />,
						iconHover: <OptionsMenuIcon classes={OPTIONS_ICON_CLASSES} hoverVariant={true} sx={{ fontSize: "29px" }} />,
						disabled: isToggleDisabled,
						loading: loadingTree,
						menus: [
							{
								label: "New From Selected",
								disabled: isCloneDisabled,
								classes: getOptionMenusClasses(isCloneDisabled),
								onClick: handleClone,
							},
							{
								label: AVAILABLE_LABEL,
								classes: getOptionMenusClasses(isMakeAvailableDisabled),
								disabled: isMakeAvailableDisabled,
								display: availableToggleLabel === AVAILABLE_LABEL,
								onClick: handleToggleAvailable,
							},
							{
								label: PROGRESS_LABEL,
								classes: getOptionMenusClasses(isMakeInProgressDisabled),
								disabled: isMakeInProgressDisabled,
								display: availableToggleLabel === PROGRESS_LABEL,
								onClick: handleToggleAvailable,
							},
						],
					},
				],
			}}
		/>
	);
}
PayPlansSideBar.propTypes = {
	showBy: PropTypes.string,
	selectBy: PropTypes.string,
	loading: PropTypes.bool,
};
